import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { X, MapPin, Heart, Calendar, Share2 } from 'lucide-react';
import L from 'leaflet';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import tourService from '../services/tourService';
import { useLocationCoords } from '../contexts/LocationContext';
import { useDistanceUnit } from '../contexts/DistanceUnitContext';
import { useTheme } from '../contexts/ThemeContext';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon for tours
const tourMarkerIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.163 0 0 7.163 0 16C0 28 16 42 16 42C16 42 32 28 32 16C32 7.163 24.837 0 16 0Z" fill="#6366F1"/>
      <circle cx="16" cy="16" r="8" fill="white"/>
      <circle cx="16" cy="16" r="5" fill="#6366F1"/>
    </svg>
  `),
  iconSize: [32, 42],
  iconAnchor: [16, 42],
  popupAnchor: [0, -42],
});

// Custom cluster icon creator
const createClusterCustomIcon = (cluster: any) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let sizeValue = 40;
  
  if (count > 50) {
    size = 'large';
    sizeValue = 60;
  } else if (count > 10) {
    size = 'medium';
    sizeValue = 50;
  }
  
  return L.divIcon({
    html: `<div class="cluster-icon cluster-${size}">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(sizeValue, sizeValue, true),
  });
};

interface Tour {
  tourId: string;
  title: string;
  description?: string;
  streetName?: string;
  city?: string;
  country?: string;
  formattedAddress?: string;
  latitude?: number;
  longitude?: number;
  distanceFromUser?: number;
  estimatedDuration?: number;
  viewCount: number;
  saveCount?: number;
  createdAt: string;
  audioStatus: string;
}

// Component to recenter map when user location becomes available
function RecenterMap({ center }: { center: [number, number] | null }) {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, 12);
    }
  }, [center, map]);
  
  return null;
}

const CommunityToursMap: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { latitude, longitude } = useLocationCoords();
  const { formatDistance } = useDistanceUnit();
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Default map center (will be updated when user location is available)
  const [mapCenter, setMapCenter] = useState<[number, number]>([51.505, -0.09]); // London default

  useEffect(() => {
    // Update map center when user location becomes available
    if (latitude !== undefined && longitude !== undefined) {
      setMapCenter([latitude, longitude]);
    }
  }, [latitude, longitude]);

  useEffect(() => {
    const fetchAllTours = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all tours with location data
        const result = await tourService.listTours(1000, 0, {
          latitude,
          longitude,
          sortByDistance: false,
          audioCompleted: true // Only show completed tours
        });

        // Filter tours that have valid coordinates
        const toursWithCoords = result.tours.filter(
          (tour: Tour) => tour.latitude !== null && tour.longitude !== null
        );

        setTours(toursWithCoords);
      } catch (err) {
        setError('Failed to load tours');
      } finally {
        setLoading(false);
      }
    };

    fetchAllTours();
  }, [latitude, longitude]);

  const handleClose = () => {
    navigate('/home');
  };

  const handleTourClick = (tourId: string) => {
    navigate(`/tour/${tourId}`, {
      state: { fromMap: true }
    });
  };

  const handleShare = async (tour: Tour, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent marker click
    
    const shareUrl = `${window.location.origin}/tour/${tour.tourId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: tour.title,
          text: `Check out this tour: ${tour.title}`,
          url: shareUrl,
        });
      } catch (err) {
        // Ignore abort errors silently
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Tour link copied to clipboard!');
      } catch (err) {
        // Silently fail
      }
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--background)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        color: 'var(--text-primary)'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '3px solid var(--border-color)',
          borderTop: '3px solid var(--primary-color)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ marginTop: '16px', fontSize: '16px' }}>Loading tours map...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--background)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        color: 'var(--text-primary)'
      }}>
        <p style={{ fontSize: '16px', marginBottom: '16px' }}>{error}</p>
        <button
          onClick={handleClose}
          style={{
            padding: '12px 24px',
            backgroundColor: 'var(--primary-color)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Community Tours Map | Miaitor</title>
        <meta name="title" content="Community Tours Map | Miaitor" />
        <meta name="description" content="Explore tours created by the Miaitor community on an interactive map." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://miaitor.com/community-tours-map" />
        <meta property="og:title" content="Community Tours Map | Miaitor" />
        <meta property="og:description" content="Explore tours created by the Miaitor community on an interactive map." />
        <meta property="og:image" content="https://miaitor.com/SocialShare.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://miaitor.com/community-tours-map" />
        <meta name="twitter:title" content="Community Tours Map | Miaitor" />
        <meta name="twitter:description" content="Explore tours created by the Miaitor community on an interactive map." />
        <meta name="twitter:image" content="https://miaitor.com/SocialShare.png" />
        
        <link rel="canonical" href="https://miaitor.com/community-tours-map" />
      </Helmet>
    
      <div style={{
        position: 'fixed',
        top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
      backgroundColor: 'var(--background)'
    }}>
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '60px',
          backgroundColor: 'var(--background)',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          zIndex: 10000,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <MapPin size={24} style={{ color: 'var(--primary-color)' }} />
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-primary)'
            }}>
              Community Tours Map
            </h2>
            <p style={{
              margin: 0,
              fontSize: '12px',
              color: 'var(--text-secondary)'
            }}>
              {tours.length} tours available
            </p>
          </div>
        </div>
        
        <button
          onClick={handleClose}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            backgroundColor: 'var(--card-background)',
            border: '1px solid var(--border-color)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--secondary-color)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--card-background)';
          }}
        >
          <X size={20} style={{ color: 'var(--text-primary)' }} />
        </button>
      </motion.div>

      {/* Map Container */}
      <div style={{
        position: 'absolute',
        top: '60px',
        left: 0,
        right: 0,
        bottom: 0
      }}>
        <style>{`
          .custom-cluster-icon {
            background: transparent;
            border: none;
          }
          
          .cluster-icon {
            background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 16px;
            border: 3px solid white;
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
            transition: all 0.3s ease;
          }
          
          .cluster-icon:hover {
            transform: scale(1.1);
            box-shadow: 0 6px 16px rgba(99, 102, 241, 0.6);
          }
          
          .cluster-small {
            width: 40px;
            height: 40px;
            font-size: 14px;
          }
          
          .cluster-medium {
            width: 50px;
            height: 50px;
            font-size: 16px;
          }
          
          .cluster-large {
            width: 60px;
            height: 60px;
            font-size: 18px;
          }
        `}</style>
        
        <MapContainer
          center={mapCenter}
          zoom={12}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url={isDarkMode 
              ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
              : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            }
          />
          
          <RecenterMap center={latitude && longitude ? [latitude, longitude] : null} />
          
          <MarkerClusterGroup
            chunkedLoading
            iconCreateFunction={createClusterCustomIcon}
            maxClusterRadius={80}
            spiderfyOnMaxZoom={true}
            showCoverageOnHover={false}
            zoomToBoundsOnClick={true}
          >
            {tours.map((tour) => {
            if (!tour.latitude || !tour.longitude) return null;
            
            const tourDate = new Date(tour.createdAt);
            const address = tour.formattedAddress || 
                           `${tour.streetName || ''}${tour.city ? ', ' + tour.city : ''}${tour.country ? ', ' + tour.country : ''}` ||
                           'Address not available';
            
            return (
              <Marker
                key={tour.tourId}
                position={[tour.latitude, tour.longitude]}
                icon={tourMarkerIcon}
              >
                <Popup>
                  <div style={{
                    minWidth: '200px',
                    padding: '8px'
                  }}>
                    <h3 style={{
                      margin: '0 0 8px 0',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1A1A1A'
                    }}>
                      {tour.title}
                    </h3>
                    
                    <p style={{
                      margin: '0 0 12px 0',
                      fontSize: '13px',
                      color: '#666',
                      lineHeight: '1.4'
                    }}>
                      {address}
                    </p>
                    
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      marginBottom: '12px',
                      fontSize: '12px',
                      color: '#666',
                      flexWrap: 'wrap'
                    }}>
                      {tour.distanceFromUser !== undefined && (
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <MapPin size={14} />
                          <span>{formatDistance(tour.distanceFromUser)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <Calendar size={14} />
                        <span>
                          {tourDate.toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: '2-digit' 
                          }).replace(/\//g, '/')}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <Heart size={14} />
                        <span>{(tour.saveCount || 0).toLocaleString()}</span>
                      </div>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      width: '100%'
                    }}>
                      <motion.button
                        onClick={() => handleTourClick(tour.tourId)}
                        whileTap={{
                          scale: 0.97,
                          filter: 'brightness(0.95)',
                          transition: { duration: 0.15, ease: "easeOut" }
                        }}
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          backgroundColor: '#6366F1',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#4F46E5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#6366F1';
                        }}
                      >
                        View Tour
                      </motion.button>
                      
                      <motion.button
                        onClick={(e) => handleShare(tour, e)}
                        whileTap={{
                          scale: 0.97,
                          filter: 'brightness(0.95)',
                          transition: { duration: 0.15, ease: "easeOut" }
                        }}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: 'rgba(99, 102, 241, 0.1)',
                          color: '#6366F1',
                          border: '1px solid #6366F1',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
                        }}
                      >
                        <Share2 size={16} />
                      </motion.button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
          </MarkerClusterGroup>
        </MapContainer>
      </div>
    </div>
    </>
  );
};

export default CommunityToursMap;

